import { AxiosInstance } from 'axios';
import { BaseApi } from 'services/api/base-api';
import { Event } from 'services/api/models';
import { EventDisplay } from 'enum/event-status.enum';
import { ApiResult } from 'services/api/result.type';
import { ListEventsResponse } from '../events/dto';
import {
  GetOrganizationResponse,
  ListOrganizationsResponse,
  GetOrganizationAdminRequestsResponse,
  GetPermissionRequestsResponse,
  ListMyOrganizationsResponse,
  ListOrganizationAttendeesParams,
  ListOrganizationAttendeesResponse,
  ListOrganizationMembersParams,
  ListOrganizationMembersResponse,
} from './dto';
import {
  SaveOrganizationRequest,
  SaveOrganizationResponse,
  UpdateOrganizationRequest,
} from './dto/save-organization.dto';

interface PendingOrgLeaderInvite {
  id: string;
  organizationId: string;
  organizationName: string;
  role: string;
  status: 'requested' | 'accepted' | 'declined';
}

export interface LeaderRequest {
  id: string;
  organizationId: string;
  organizationName?: string;
  role: string;
  status: 'requested' | 'accepted' | 'declined';
  inviteSource: 'admin' | 'user';
  createdAt: string;
  lastStatusChangedDate?: string;
}

export interface MemberRequest {
  id: string | null;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

export class OrganizationsApi extends BaseApi {
  public constructor (protected readonly api: AxiosInstance) {
    super();

    this.getOrganization = this.getOrganization.bind(this);
    this.createOrganization = this.createOrganization.bind(this);
    this.saveOrganization = this.saveOrganization.bind(this);
    this.deleteOrganization = this.deleteOrganization.bind(this);
    this.listOrganizations = this.listOrganizations.bind(this);
    this.addLeaderToOrganization = this.addLeaderToOrganization.bind(this);
    this.removeLeaderFromOrganization = this.removeLeaderFromOrganization.bind(
      this,
    );
    this.listOrganizationEvents = this.listOrganizationEvents.bind(this);
    this.followOrganization = this.followOrganization.bind(this);
    this.unfollowOrganization = this.unfollowOrganization.bind(this);
    this.getFeedbackSummary = this.getFeedbackSummary.bind(this);
    this.downloadEventFeedback = this.downloadEventFeedback.bind(this);
    this.exportAllFeedback = this.exportAllFeedback.bind(this);
  }

  public async getOrganization (
    organizationId: string,
  ): ApiResult<GetOrganizationResponse> {
    return await this.get<GetOrganizationResponse>(
      `/organizations/${organizationId}`,
    );
  }

  public async createOrganization ({
    schoolId,
    name,
    logoUrl,
    leaders,
  }: SaveOrganizationRequest): ApiResult<SaveOrganizationResponse> {
    return await this.post<SaveOrganizationResponse>('/organizations', {
      schoolId,
      name,
      logoUrl,
      leaders,
    });
  }

  public async saveOrganization (
    organizationId: string,
    {
      name,
      logoUrl,
      tagline,
      missionStatement,
      types,
      leaders,
      membershipApprovalMode,
      status,
    }: UpdateOrganizationRequest,
  ): ApiResult<SaveOrganizationResponse> {
    const body: UpdateOrganizationRequest = {};
    if (name !== undefined) body.name = name;
    if (logoUrl !== undefined) body.logoUrl = logoUrl;
    if (tagline !== undefined) body.tagline = tagline;
    if (missionStatement !== undefined) body.missionStatement = missionStatement;
    if (types !== undefined) body.types = types;
    if (leaders !== undefined) body.leaders = leaders;
    if (membershipApprovalMode !== undefined)
      body.membershipApprovalMode = membershipApprovalMode;
    if (status !== undefined) body.status = status;

    return await this.patch<SaveOrganizationResponse>(
      `/organizations/${organizationId}`,
      body,
    );
  }

  public async deleteOrganization (organizationId: string): ApiResult<void> {
    return await this.delete<void>(`/organizations/${organizationId}`);
  }

  async listOrganizations ({
    page,
    pageSize,
    search,
    cursor,
  }: {
    page?: number;
    pageSize?: number;
    search?: string | null;
    cursor?: string | null;
  }): ApiResult<ListOrganizationsResponse> {
    const params: {
      page?: number;
      pageSize?: number;
      search?: string | null;
      cursor?: string | null;
    } = {
      pageSize,
      search,
    };

    if (cursor) params.cursor = cursor;
    else params.page = page;

    return await this.get<ListOrganizationsResponse>('/organizations', {
      params,
    });
  }

  public async addLeaderToOrganization (
    organizationId: string,
    userId: string,
  ): ApiResult<void> {
    return await this.post<void>(`/organizations/${organizationId}/addLeader`, {
      userId,
    });
  }

  public async removeLeaderFromOrganization (
    organizationId: string,
    userId: string,
    options?: { removeFromOrganization?: boolean },
  ): ApiResult<void> {
    return await this.post<void>(
      `/organizations/${organizationId}/removeLeader`,
      {
        userId,
        removeFromOrganization: Boolean(options?.removeFromOrganization),
      },
    );
  }

  public async cancelAdminLeaderInvite (
    organizationId: string,
    leaderId: string,
  ): ApiResult<void> {
    return await this.post<void>(
      `/organizations/${organizationId}/leaders/${leaderId}/cancel-invite`,
    );
  }

  public async adminUpdatePendingLeaderRequestRole (
    leaderId: string,
    role: string,
  ): ApiResult<void> {
    return await this.patch<void>(
      `/organizations/leader-requests/${leaderId}/admin`,
      { role },
    );
  }

  public async removeAdminFromOrganization (
    organizationId: string,
    userId: string,
  ): ApiResult<void> {
    return await this.post<void>(
      `/organizations/${organizationId}/removeAdmin`,
      {
        userId,
      },
    );
  }

  public async listOrganizationEvents (
    organizationId: string,
    {
      page,
      pageSize,
      search,
      statuses,
      displayFilter,
      includeCardMeta,
    }: {
      page?: number;
      pageSize?: number;
      search?: string | null;
      statuses?: Event['status'][];
      displayFilter?: EventDisplay;
      includeCardMeta?: boolean;
    },
  ): ApiResult<ListEventsResponse> {
    return await this.get<ListEventsResponse>(
      `/organizations/${organizationId}/events`,
      {
        params: {
          page,
          pageSize,
          search,
          statuses,
          displayFilter,
          includeCardMeta,
        },
      },
    );
  }

  public async followOrganization (id: string): ApiResult<void> {
    return await this.post(`/organizations/${id}/follow`);
  }

  public async unfollowOrganization (id: string): ApiResult<void> {
    return await this.post(`/organizations/${id}/unfollow`);
  }

  public async requestAdminAccess (organizationId: string): ApiResult<void> {
    return await this.post(
      `/organizations/${organizationId}/requestAdminAccess`,
    );
  }

  public async getOrganizationAdminRequests (
    page?: number,
    pageSize?: number,
    cursor?: string,
  ): ApiResult<GetOrganizationAdminRequestsResponse> {
    const params: {
      page?: number;
      pageSize?: number;
      cursor?: string;
    } = {
      pageSize,
    };

    if (cursor) params.cursor = cursor;
    else params.page = page;

    return await this.get('/organizations/admin-requests', {
      params,
    });
  }

  public async acceptAdminRequest (requestId: string): ApiResult<void> {
    return await this.post(`/organizations/admin-requests/${requestId}/accept`);
  }

  public async declineAdminRequest (requestId: string): ApiResult<void> {
    return await this.post(
      `/organizations/admin-requests/${requestId}/decline`,
    );
  }

  // New unified permission requests API
  public async getPermissionRequests (): ApiResult<GetPermissionRequestsResponse> {
    return await this.get('/permission-requests');
  }

  public async getPermissionRequestsCount (): ApiResult<{ count: number }> {
    return await this.get<{ count: number }>('/permission-requests/count');
  }

  /** Organizations the current user is affiliated with (admin, leader, or member). */
  public async getMyOrganizations (): ApiResult<ListMyOrganizationsResponse> {
    return await this.get<ListMyOrganizationsResponse>('/organizations/me');
  }

  public async acceptPermissionRequest (
    requestId: string,
    requestType: 'admin' | 'leader' | 'member',
  ): ApiResult<void> {
    if (requestType === 'admin')
      return await this.post(`/organizations/admin-requests/${requestId}/accept`);
    if (requestType === 'member')
      return await this.post(`/organizations/member-requests/${requestId}/accept`);

    return await this.post(`/organizations/leader-requests/${requestId}/accept`);
  }

  public async declinePermissionRequest (
    requestId: string,
    requestType: 'admin' | 'leader' | 'member',
  ): ApiResult<void> {
    if (requestType === 'admin')
      return await this.post(`/organizations/admin-requests/${requestId}/decline`);
    if (requestType === 'member')
      return await this.post(`/organizations/member-requests/${requestId}/decline`);

    return await this.post(`/organizations/leader-requests/${requestId}/decline`);
  }

  public async inviteLeader (
    organizationId: string,
    userId: string,
    role: string,
  ): ApiResult<void> {
    return await this.post(`/organizations/${organizationId}/invite-leader`, {
      userId,
      role,
    });
  }

  public async acceptLeaderInvite (
    leaderId: string,
    acceptedTerms: boolean,
  ): ApiResult<void> {
    return await this.post(`/organizations/leaders/${leaderId}/accept`, {
      acceptedTerms,
    });
  }

  public async declineLeaderInvite (leaderId: string): ApiResult<void> {
    return await this.post(`/organizations/leaders/${leaderId}/decline`);
  }

  public async editLeaderRole (leaderId: string, role: string): ApiResult<void> {
    return await this.patch(`/organizations/leaders/${leaderId}/role`, {
      role,
    });
  }

  public async getPendingOrgLeaderInvites (): ApiResult<
    PendingOrgLeaderInvite[]
  > {
    return await this.get('/organizations/leaders/me/pending-invites');
  }

  public async reInviteLeader (leaderId: string): ApiResult<void> {
    return await this.post(`/organizations/leaders/${leaderId}/re-invite`);
  }

  public async getAllOrgLeaderInvites (): ApiResult<PendingOrgLeaderInvite[]> {
    return await this.get('/organizations/leaders/me/all-invites');
  }

  // User-initiated leader request methods

  /**
   * User requests to become a leader of an organization
   */
  public async requestLeaderAccess (
    organizationId: string,
    role: string,
  ): ApiResult<LeaderRequest> {
    return await this.post(`/organizations/${organizationId}/request-leader`, {
      role,
    });
  }

  /**
   * User updates their pending leader request (change role)
   */
  public async updateLeaderRequest (
    requestId: string,
    role: string,
  ): ApiResult<LeaderRequest> {
    return await this.patch(`/organizations/leader-requests/${requestId}`, {
      role,
    });
  }

  /**
   * User cancels their pending leader request
   */
  public async cancelLeaderRequest (requestId: string): ApiResult<void> {
    return await this.delete(`/organizations/leader-requests/${requestId}`);
  }

  /**
   * Get user's leader request for a specific organization
   */
  public async getMyLeaderRequestForOrg (
    organizationId: string,
  ): ApiResult<LeaderRequest | null> {
    return await this.get(`/organizations/${organizationId}/my-leader-request`);
  }

  // User-initiated membership (general-member) request methods (CAM-669)

  /**
   * User requests to become a general member of an organization
   */
  public async requestMembership (
    organizationId: string,
  ): ApiResult<MemberRequest> {
    return await this.post(
      `/organizations/${organizationId}/request-membership`,
    );
  }

  /**
   * User cancels their pending membership request
   */
  public async cancelMembershipRequest (requestId: string): ApiResult<void> {
    return await this.delete(
      `/organizations/member-requests/${requestId}`,
    );
  }

  /**
   * Get user's pending membership request for a specific organization
   */
  public async getMyMembershipRequestForOrg (
    organizationId: string,
  ): ApiResult<MemberRequest | null> {
    return await this.get(
      `/organizations/${organizationId}/my-membership-request`,
    );
  }

  public async getFeedbackSummary (
    organizationId: string,
    params?: {
      page?: number;
      pageSize?: number;
      search?: string;
      academicYearStart?: string;
      academicYearEnd?: string;
    },
  ): ApiResult<{
    events: Array<{
      eventId: string;
      eventName: string;
      eventDate: string;
      flyerImageUrl: string;
      averageRating: number | null;
      voteCount: number;
    }>;
    total: number;
  }> {
    return await this.get(
      `/organizations/${organizationId}/feedback`,
      { params },
    );
  }

  /** Executive-admin: feedback summary across all orgs in the user's school */
  public async getSchoolWideFeedbackSummary (
    params?: {
      page?: number;
      pageSize?: number;
      search?: string;
      academicYearStart?: string;
      academicYearEnd?: string;
      organizationId?: string;
    },
  ): ApiResult<{
    events: Array<{
      eventId: string;
      eventName: string;
      eventDate: string;
      flyerImageUrl: string;
      averageRating: number | null;
      voteCount: number;
      primaryOrganizationId: string | null;
      organizationName: string | null;
    }>;
    total: number;
  }> {
    return await this.get('/organizations/school-wide/feedback', { params });
  }

  public async downloadEventFeedback (
    organizationId: string,
    eventId: string,
  ): Promise<void> {
    const response = await this.api.get(
      `/organizations/${organizationId}/feedback/${eventId}/download`,
      {
        responseType: 'blob',
      },
    );

    // Create blob and download
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `feedback-${eventId.substring(0, 8)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  public async exportAllFeedback (
    organizationId: string,
    params?: {
      academicYearStart?: string;
      academicYearEnd?: string;
    },
  ): Promise<void> {
    const response = await this.api.get(
      `/organizations/${organizationId}/feedback/export`,
      {
        params,
        responseType: 'blob',
      },
    );

    // Create blob and download
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `all-feedback-${organizationId.substring(0, 8)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  public async listOrganizationMembers (
    organizationId: string,
    params: ListOrganizationMembersParams = {},
  ): ApiResult<ListOrganizationMembersResponse> {
    return await this.get<ListOrganizationMembersResponse>(
      `/organizations/${organizationId}/members`,
      { params },
    );
  }

  public async listOrganizationAttendees (
    organizationId: string,
    params: ListOrganizationAttendeesParams = {},
  ): ApiResult<ListOrganizationAttendeesResponse> {
    return await this.get<ListOrganizationAttendeesResponse>(
      `/organizations/${organizationId}/attendees`,
      { params },
    );
  }

  public async removeOrganizationMember (
    organizationId: string,
    userId: string,
  ): ApiResult<void> {
    return await this.post<void>(
      `/organizations/${organizationId}/members/${userId}/remove`,
    );
  }

  public async exportSchoolWideFeedback (
    params?: {
      academicYearStart?: string;
      academicYearEnd?: string;
      organizationId?: string;
    },
  ): Promise<void> {
    const response = await this.api.get(
      '/organizations/school-wide/feedback/export',
      {
        params,
        responseType: 'blob',
      },
    );

    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'all-feedback-school.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
